import React, { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Button,
  Tabs,
  Tab,
  Skeleton,
  Divider,
  Badge,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Switch,
  FormControlLabel,
  Checkbox,
  Collapse,
  Popover,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import PageHeader from '../../ui-components/PageHeader';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderIcon from '@mui/icons-material/Folder';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningIcon from '@mui/icons-material/Warning';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import MovieIcon from '@mui/icons-material/Movie';
import DescriptionIcon from '@mui/icons-material/Description';
import PhoneIcon from '@mui/icons-material/Phone';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SendIcon from '@mui/icons-material/Send';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideocamIcon from '@mui/icons-material/Videocam';
import { BACKEND_URL, AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { initMicrosoftTodoService, getMyTodos, completeTask, reopenTask, createTask as createMsTodoTask, todoDueDate } from '../../utils/microsoftTodoService';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import AddIcon from '@mui/icons-material/Add';
import { formatDateISO, getMonday } from '../../utils/weekUtils';
import { AFGEROND_SET, isDagstaatTaak } from '../../utils/taakStatus';
import { dagenTot } from '../../utils/nlDatum';

// Role mapping based on Airtable Functie field
const ROLE_MAPPING = {
  PL: ['Projectleider'],
  UITVOERDER: ['Editor', 'Animator', 'Cameraman', 'Camera Operator', 'Colorist / Grader', 'Geluidstechnicus', 'Illustrator'],
  CREATIVE: ['Regisseur', 'Creatief Strateeg', 'Art Director', 'Copywriter'],
  PRODUCER: ['Producer', 'Productie-assistent', 'Opnameleider', 'Planner'],
  MANAGEMENT: ['MT', 'Accountmanager', 'HR', 'Boekhouder'],
};

// Get role category from function list
const getRoleCategory = (functions) => {
  const arr = Array.isArray(functions) ? functions : (typeof functions === 'string' && functions ? functions.split(', ') : []);
  if (arr.length === 0) return 'UITVOERDER'; // Default

  for (const [role, funcs] of Object.entries(ROLE_MAPPING)) {
    if (arr.some(f => funcs.includes(f))) {
      return role;
    }
  }
  return 'UITVOERDER';
};

// Color schemes
const roleColors = {
  PL: { primary: '#3b82f6', bg: '#dbeafe' },
  UITVOERDER: { primary: '#10b981', bg: '#dcfce7' },
  CREATIVE: { primary: '#8b5cf6', bg: '#ede9fe' },
  PRODUCER: { primary: '#f59e0b', bg: '#fef3c7' },
  MANAGEMENT: { primary: '#ef4444', bg: '#fee2e2' },
};

const taskTypeColors = {
  'Montage': '#10b981',
  'Animatie': '#ec4899',
  'Preproductie': '#818cf8',
  'Draaidag': '#f59e0b',
  'Regie': '#7c3aed',
  'Deadline': '#ef4444',
};

// Helper functions
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
};

const isToday = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const today = new Date();
  return d.toDateString() === today.toDateString();
};

const isThisWeek = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const weekStart = getMonday(new Date()); // FE-08: op zondag sprong dit naar de volgende week
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  return d >= weekStart && d <= weekEnd;
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

// Quick Link Card Component
function QuickLinkCard({ title, description, icon: Icon, to, color, badge }) {
  const navigate = useNavigate();
  
  return (
    <Card
      onClick={() => navigate(to)}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: '1px solid #e5e7eb',
        '&:hover': { 
          transform: 'translateY(-2px)', 
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          borderColor: color,
        },
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        <Badge badgeContent={badge} color="error" invisible={!badge}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ color, fontSize: 24 }} />
          </Box>
        </Badge>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{description}</Typography>
        </Box>
        <ArrowForwardIcon sx={{ color: '#d1d5db' }} />
      </CardContent>
    </Card>
  );
}

// Task status flow via PrioNr field: (any prio) → "Afgerond" → "Opgestuurd"
// Note: PrioNr contains both priority values (Prio 1-5) and status values (Afgerond, Opgestuurd)
const getNextPrioStatus = (currentPrioNr) => {
  if (currentPrioNr === 'Opgestuurd') {
    return null; // Can't go further
  }
  if (currentPrioNr === 'Afgerond') {
    return { PrioNr: 'Opgestuurd', Afgerond: true };
  }
  // Any other value (Prio 1-5, etc.) → Afgerond
  return { PrioNr: 'Afgerond', Afgerond: true };
};

const getStatusLabel = (prioNr, isComplete) => {
  if (prioNr === 'Opgestuurd') return { label: 'Opgestuurd', color: '#8b5cf6', bg: '#ede9fe' };
  if (prioNr === 'Afgerond' || isComplete) return { label: 'Afgerond', color: '#10b981', bg: '#dcfce7' };
  return null;
};

// All available PrioNr statuses
const PRIO_OPTIONS = [
  { value: 'Prio 1', label: 'Prio 1', color: '#ef4444', bg: '#fee2e2' },
  { value: 'Prio 2', label: 'Prio 2', color: '#ef4444', bg: '#fee2e2' },
  { value: 'Prio 3', label: 'Prio 3', color: '#f59e0b', bg: '#fef3c7' },
  { value: 'Prio 4', label: 'Prio 4', color: '#f59e0b', bg: '#fef3c7' },
  { value: 'Geen prio', label: 'Geen prio', color: '#3b82f6', bg: '#dbeafe' },
  { value: 'Deadline', label: 'Deadline', color: '#ef4444', bg: '#fee2e2' },
  { value: 'Wacht op feedback', label: 'Wacht op feedback', color: '#f97316', bg: '#ffedd5' },
  { value: 'Nog niet van toepassing', label: 'Nog niet van toepassing', color: '#14b8a6', bg: '#ccfbf1' },
  { value: 'Afgerond', label: 'Afgerond', color: '#10b981', bg: '#dcfce7' },
  { value: 'Opgestuurd', label: 'Opgestuurd', color: '#8b5cf6', bg: '#ede9fe' },
];

// Task Detail Dialog for Dashboard
function TaskDetailModal({ task, open, onClose, onStatusUpdate }) {
  const [updating, setUpdating] = useState(false);

  if (!task) return null;

  const name = task.fields?.NaamTaak || '-';
  const type = task.fields?.Type || '';
  const prioNr = task.fields?.PrioNr || '';
  const deadline = task.fields?.Deadline;
  const startDate = task.fields?.StartDateUitvoerder;
  const endDate = task.fields?.EndDateUitvoerder;
  const assignees = task.fields?.['Voornaam Assignee'] || [];
  const projectNumber = task.fields?.ProjectNummer?.[0] || '';
  const deliverable = task.fields?.Deliverable?.[0] || '';
  const opmerking = task.fields?.Opmerking || '';

  const handlePrioChange = async (newPrio) => {
    if (!onStatusUpdate || updating) return;
    setUpdating(true);
    const isAfgerond = newPrio === 'Afgerond' || newPrio === 'Opgestuurd';
    await onStatusUpdate(task.id, { PrioNr: newPrio, Afgerond: isAfgerond });
    setUpdating(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <AssignmentIcon sx={{ color: taskTypeColors[type] || '#3b82f6', mt: 0.5 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600}>{name}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              {type && <Chip label={type} size="small" sx={{ backgroundColor: `${taskTypeColors[type] || '#e5e7eb'}20`, color: taskTypeColors[type] || '#6b7280' }} />}
            </Box>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Status/Priority Selector */}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Status / Prioriteit</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {PRIO_OPTIONS.map(opt => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  size="small"
                  onClick={() => handlePrioChange(opt.value)}
                  disabled={updating}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: prioNr === opt.value ? 700 : 400,
                    backgroundColor: prioNr === opt.value ? opt.bg : '#f3f4f6',
                    color: prioNr === opt.value ? opt.color : '#6b7280',
                    border: prioNr === opt.value ? `2px solid ${opt.color}` : '2px solid transparent',
                    '&:hover': { backgroundColor: opt.bg, color: opt.color },
                  }}
                />
              ))}
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Project</Typography>
            <Typography variant="body2" fontWeight={500}>{projectNumber || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Uitvoerder(s)</Typography>
            <Typography variant="body2" fontWeight={500}>{assignees.join(', ') || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Startdatum</Typography>
            <Typography variant="body2">{formatDate(startDate) || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Einddatum</Typography>
            <Typography variant="body2">{formatDate(endDate) || '-'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Deadline</Typography>
            <Typography variant="body2" color={deadline && new Date(deadline) < new Date() ? 'error' : 'inherit'}>
              {formatDate(deadline) || '-'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Deliverable</Typography>
            <Typography variant="body2">{deliverable || '-'}</Typography>
          </Grid>
          {opmerking && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Opmerking</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{opmerking}</Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Sluiten</Button>
      </DialogActions>
    </Dialog>
  );
}

// Task Card Component
function TaskCard({ task, compact = false, onClick, onStatusUpdate, showProjectName = false, showAssignee = false }) {
  const [updating, setUpdating] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  
  const name = task.fields?.NaamTaak || '-';
  const type = task.fields?.Type || '';
  const isComplete = task.fields?.Afgerond === true;
  const prioNr = task.fields?.PrioNr || '';
  const deadline = task.fields?.Deadline;
  const startDate = task.fields?.StartDateUitvoerder;
  const projectNumber = task.fields?.ProjectNummer?.[0] || '';
  const assignees = task.fields?.['Voornaam Assignee'] || [];
  
  // Draaidag of vrije dag: voorbij is voorbij, nooit te laat (Joris, 3 nov 2026).
  const isOverdue = deadline && new Date(deadline) < new Date() && !isComplete && prioNr !== 'Opgestuurd' && prioNr !== 'Afgerond' && !isDagstaatTaak(task);
  const isDueToday = isToday(deadline) || isToday(startDate);
  const statusInfo = getStatusLabel(prioNr, isComplete);
  const canUpdate = prioNr !== 'Opgestuurd';

  const handleCheckClick = async (e) => {
    e.stopPropagation();
    if (!onStatusUpdate || !canUpdate || updating) return;
    
    setUpdating(true);
    const nextStatus = getNextPrioStatus(prioNr);
    if (nextStatus) {
      await onStatusUpdate(task.id, nextStatus);
    }
    setUpdating(false);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(task);
    } else {
      setDetailOpen(true);
    }
  };

  return (
    <>
      <TaskDetailModal task={task} open={detailOpen} onClose={() => setDetailOpen(false)} onStatusUpdate={onStatusUpdate} />
      <Paper
        onClick={handleCardClick}
        sx={{
          p: compact ? 1.5 : 2,
          cursor: 'pointer',
          borderLeft: `3px solid ${taskTypeColors[type] || '#d1d5db'}`,
          backgroundColor: prioNr === 'Opgestuurd' ? '#faf5ff' : isComplete || prioNr === 'Afgerond' ? '#f0fdf4' : isOverdue ? '#fef2f2' : isDueToday ? '#fffbeb' : '#fff',
          transition: 'all 0.15s',
          opacity: updating ? 0.7 : 1,
          '&:hover': { backgroundColor: '#f9fafb', transform: 'translateX(2px)' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Tooltip title={
            !canUpdate ? 'Al opgestuurd' : 
            prioNr === 'Afgerond' ? '📤 Klik om als opgestuurd te markeren' : 
            'Markeer als afgerond'
          }>
            <Box
              onClick={handleCheckClick}
              sx={{ 
                cursor: canUpdate && onStatusUpdate ? 'pointer' : 'default',
                '&:hover': canUpdate && onStatusUpdate ? { transform: 'scale(1.2)' } : {},
                transition: 'transform 0.15s',
              }}
            >
              {prioNr === 'Opgestuurd' ? (
                <CheckCircleIcon sx={{ fontSize: 20, color: '#8b5cf6', mt: 0.25 }} />
              ) : prioNr === 'Afgerond' ? (
                // Afgerond: show send icon to indicate next action
                <SendIcon sx={{ fontSize: 20, color: '#10b981', mt: 0.25 }} />
              ) : isComplete ? (
                <CheckCircleIcon sx={{ fontSize: 20, color: '#10b981', mt: 0.25 }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: '#d1d5db', mt: 0.25 }} />
              )}
            </Box>
          </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              textDecoration: prioNr === 'Opgestuurd' ? 'line-through' : 'none',
              color: prioNr === 'Opgestuurd' ? '#9ca3af' : '#111827',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: compact ? 'nowrap' : 'normal',
            }}
          >
            {name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {type && (
              <Chip
                label={type}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  backgroundColor: `${taskTypeColors[type] || '#e5e7eb'}20`,
                  color: taskTypeColors[type] || '#6b7280',
                }}
              />
            )}
            {statusInfo && (
              <Chip
                label={statusInfo.label}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  backgroundColor: statusInfo.bg,
                  color: statusInfo.color,
                  fontWeight: 600,
                }}
              />
            )}
            {(deadline || startDate) && (
              <Typography variant="caption" color={isOverdue ? 'error' : 'text.secondary'}>
                {isOverdue && <WarningIcon sx={{ fontSize: 12, mr: 0.25, verticalAlign: 'middle' }} />}
                {formatDate(deadline || startDate)}
              </Typography>
            )}
            {showProjectName && projectNumber && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                📁 {projectNumber}
              </Typography>
            )}
            {showAssignee && assignees.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: showProjectName ? 0 : 'auto' }}>
                👤 {assignees.join(', ')}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
    </>
  );
}

// Status badge configs for PL workflow
const statusConfigs = {
  // 1. Concepting/Scripting status
  concepting: {
    afgerond: { label: 'Concept ✓', color: '#166534', bg: '#dcfce7', icon: '📝' },
    ingepland: { label: 'Concept gepland', color: '#1e40af', bg: '#dbeafe', icon: '📝' },
    niet_ingepland: { label: 'Concept?', color: '#6b7280', bg: '#f3f4f6', icon: '📝' },
  },
  // 2. Deliverables status
  deliverables: {
    aangemaakt: { label: 'Deliverables', color: '#166534', bg: '#dcfce7', icon: '📦' },
    geen: { label: 'Geen deliverables', color: '#dc2626', bg: '#fee2e2', icon: '📦' },
  },
  // 3. Draaidagen status
  draaidagen: {
    ingepland: { label: 'Draaidag(en)', color: '#166534', bg: '#dcfce7', icon: '🎬' },
    geen: { label: '', color: '#6b7280', bg: '#f3f4f6', icon: '' }, // Don't show if no draaidagen
  },
  // 4. Postproductie/Montage status
  montage: {
    geen_montage_nodig: { label: 'Geen montage', color: '#6b7280', bg: '#f3f4f6', icon: '⬜' },
    montage_ingepland: { label: 'Montage ✓', color: '#166534', bg: '#dcfce7', icon: '🗓️' },
    alleen_animatie: { label: 'Animatie', color: '#a16207', bg: '#fef3c7', icon: '🎨' },
    geen_taken: { label: 'Geen post taken', color: '#dc2626', bg: '#fee2e2', icon: '❌' },
    niet_ingepland: { label: 'Post?', color: '#dc2626', bg: '#fee2e2', icon: '🟥' },
    niet_van_toepassing: { label: '', color: '#6b7280', bg: '#f3f4f6', icon: '' },
  },
  // 5. Voorbereiding status
  prep: {
    geen_nodig: { label: 'Geen prep', color: '#6b7280', bg: '#f3f4f6', icon: '⬜' },
    voorbereid: { label: 'Prep ✓', color: '#166534', bg: '#dcfce7', icon: '✅' },
    deels_voorbereid: { label: 'Prep deels', color: '#a16207', bg: '#fef3c7', icon: '🟨' },
    vooruit_ingevuld: { label: 'Vooruit ✓', color: '#a16207', bg: '#fef3c7', icon: '🟡' },
    nog_niks: { label: 'Prep?', color: '#dc2626', bg: '#fee2e2', icon: '🟥' },
    niet_van_toepassing: { label: '', color: '#6b7280', bg: '#f3f4f6', icon: '' },
  },
  // 6. Callsheet status
  callsheet: {
    compleet: { label: 'Callsheet ✓', color: '#166534', bg: '#dcfce7', icon: '📋' },
    deels_aangemaakt: { label: 'Callsheet...', color: '#a16207', bg: '#fef3c7', icon: '📋' },
    nog_geen: { label: 'Callsheet?', color: '#dc2626', bg: '#fee2e2', icon: '📋' },
    niet_van_toepassing: { label: '', color: '#6b7280', bg: '#f3f4f6', icon: '' },
  },
};

// Legacy configs for backwards compatibility
const prepStatusConfig = statusConfigs.prep;
const montageStatusConfig = statusConfigs.montage;

// Project Card Component with full PL workflow status
function ProjectCard({ project, taskCount, completedCount, onClick, projectStatus }) {
  const name = project.fields?.Projectnr_en_naam || project.fields?.Projectnaam || 'Project';
  const client = project.fields?.KlantnaamString || '';
  const progress = taskCount > 0 ? (completedCount / taskCount) * 100 : 0;
  
  // Get status configs for each workflow step
  const getConfig = (category, status) => {
    return statusConfigs[category]?.[status] || { label: '', color: '#6b7280', bg: '#f3f4f6', icon: '' };
  };
  
  const conceptConfig = getConfig('concepting', projectStatus?.conceptingStatus);
  const delivConfig = getConfig('deliverables', projectStatus?.deliverablesStatus);
  const draaiConfig = getConfig('draaidagen', projectStatus?.draaidagenStatus);
  const montageConfig = getConfig('montage', projectStatus?.montageStatus);
  const prepConfig = getConfig('prep', projectStatus?.prepStatus);
  const callsheetConfig = getConfig('callsheet', projectStatus?.callsheetStatus);
  
  const hasDraaidagen = projectStatus?.draaidagenCount > 0;

  // Build list of badges to show
  const badges = [];
  
  // Always show deliverables status
  if (delivConfig.label) badges.push({ key: 'deliv', ...delivConfig });
  
  // Show draaidagen count if any
  if (hasDraaidagen) {
    badges.push({ 
      key: 'draai', 
      label: `${projectStatus.draaidagenCount} draaidag${projectStatus.draaidagenCount > 1 ? 'en' : ''}`,
      color: '#166534', bg: '#dcfce7', icon: '🎬'
    });
  }
  
  // Show prep status only if draaidagen exist
  if (hasDraaidagen && prepConfig.label) badges.push({ key: 'prep', ...prepConfig });
  
  // Show montage status only if relevant
  if (montageConfig.label) badges.push({ key: 'montage', ...montageConfig });
  
  // Show callsheet status only if draaidagen exist
  if (hasDraaidagen && callsheetConfig.label) badges.push({ key: 'callsheet', ...callsheetConfig });

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2,
        cursor: onClick ? 'pointer' : 'default',
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        transition: 'all 0.15s',
        '&:hover': onClick ? { borderColor: '#3b82f6', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' } : {},
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} noWrap>{name}</Typography>
          {client && <Typography variant="caption" color="text.secondary">{client}</Typography>}
        </Box>
        {taskCount > 0 && (
          <Chip
            label={`${completedCount}/${taskCount}`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              backgroundColor: progress === 100 ? '#dcfce7' : '#f3f4f6',
              color: progress === 100 ? '#166534' : '#6b7280',
            }}
          />
        )}
      </Box>
      
      {/* PL Workflow Status badges */}
      {badges.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5, mb: 1 }}>
          {badges.map(badge => (
            <Tooltip key={badge.key} title={badge.label}>
              <Chip
                label={`${badge.icon} ${badge.label}`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.55rem',
                  backgroundColor: badge.bg,
                  color: badge.color,
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            </Tooltip>
          ))}
        </Box>
      )}
      
      {taskCount > 0 && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: '#e5e7eb',
            '& .MuiLinearProgress-bar': {
              backgroundColor: progress === 100 ? '#10b981' : '#3b82f6',
            },
          }}
        />
      )}
    </Paper>
  );
}

// ===== UITVOERDER DASHBOARD =====
function UitvoerderDashboard({ user, tasks, projects, projectStatuses, onStatusUpdate, draaidagen }) {
  const navigate = useNavigate();
  
  // Filter tasks for this user
  const myTasks = useMemo(() => {
    if (!user?.voornaam) return [];
    return tasks.filter(t => {
      const assignees = t.fields?.['Voornaam Assignee'] || [];
      return assignees.some(a => a?.toLowerCase().includes(user.voornaam?.toLowerCase()));
    });
  }, [tasks, user]);

  // Filter out completed/sent tasks
  const activeTasks = myTasks.filter(t => t.fields?.PrioNr !== 'Opgestuurd');
  const todayTasks = activeTasks.filter(t => isToday(t.fields?.StartDateUitvoerder) || isToday(t.fields?.Deadline));
  const weekTasks = activeTasks.filter(t => !isToday(t.fields?.StartDateUitvoerder) && isThisWeek(t.fields?.StartDateUitvoerder));
  const incompleteTasks = activeTasks.filter(t => !t.fields?.Afgerond && t.fields?.PrioNr !== 'Afgerond');

  // Group tasks by project helper
  const groupByProject = (taskList) => {
    const groups = {};
    taskList.forEach(t => {
      const projectNr = t.fields?.ProjectNummer?.[0] || 'Geen project';
      if (!groups[projectNr]) groups[projectNr] = [];
      groups[projectNr].push(t);
    });
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  };

  const todayByProject = useMemo(() => groupByProject(todayTasks), [todayTasks]);
  const weekByProject = useMemo(() => groupByProject(weekTasks), [weekTasks]);

  // Check if user is cameraman
  const isCameraman = user?.functies?.some(f => ['Cameraman', 'Camera Operator'].includes(f));

  return (
    <Box>
      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#dbeafe' }}>
            <Typography variant="h4" fontWeight={700} color="primary">{todayTasks.length}</Typography>
            <Typography variant="caption" color="text.secondary">Vandaag</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#dcfce7' }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#10b981' }}>{weekTasks.length}</Typography>
            <Typography variant="caption" color="text.secondary">Deze week</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fef3c7' }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#f59e0b' }}>{incompleteTasks.length}</Typography>
            <Typography variant="caption" color="text.secondary">Open taken</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f3f4f6' }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#6b7280' }}>{projects.length}</Typography>
            <Typography variant="caption" color="text.secondary">Projecten</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Links for Cameramen */}
      {isCameraman && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
            SNELLE LINKS
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <QuickLinkCard
                title="Materiaal Reserveren"
                description="Reserveer camera's en apparatuur"
                icon={CameraAltIcon}
                to="/MaterialReservation"
                color="#3b82f6"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <QuickLinkCard
                title="Ondertitels Genereren"
                description="SRT en FCP-XML maken"
                icon={SubtitlesIcon}
                to="/SRTGenerator"
                color="#8b5cf6"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Today's Tasks - grouped by project */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TodayIcon sx={{ color: '#3b82f6' }} />
              <Typography variant="subtitle1" fontWeight={700}>Vandaag</Typography>
              <Chip label={todayTasks.length} size="small" sx={{ ml: 'auto' }} />
            </Box>
            {todayTasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                Geen taken voor vandaag!
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {todayByProject.map(([projectNr, projectTasks]) => (
                  <Box key={projectNr}>
                    {todayByProject.length > 1 && (
                      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <FolderIcon sx={{ fontSize: 14 }} /> {projectNr}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {projectTasks.map(task => (
                        <TaskCard key={task.id} task={task} compact onStatusUpdate={onStatusUpdate} />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* This Week - grouped by project */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <DateRangeIcon sx={{ color: '#10b981' }} />
              <Typography variant="subtitle1" fontWeight={700}>Rest van de week</Typography>
              <Chip label={weekTasks.length} size="small" sx={{ ml: 'auto' }} />
            </Box>
            {weekTasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                Geen andere taken deze week
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {weekByProject.map(([projectNr, projectTasks]) => (
                  <Box key={projectNr}>
                    {weekByProject.length > 1 && (
                      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <FolderIcon sx={{ fontSize: 14 }} /> {projectNr}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {projectTasks.map(task => (
                        <TaskCard key={task.id} task={task} compact onStatusUpdate={onStatusUpdate} />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Aankomende Draaidagen (for crew members) */}
        {draaidagen?.length > 0 && (
          <Grid item xs={12} md={6}>
            <AankomendeDraaidagen draaidagen={draaidagen} userEmail={user?.email} userNaam={user?.naam} />
          </Grid>
        )}

        {/* Projects Overview */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FolderIcon sx={{ color: '#6b7280' }} />
              <Typography variant="subtitle1" fontWeight={700}>Mijn Projecten</Typography>
              <Button size="small" onClick={() => navigate('/projects')} sx={{ ml: 'auto' }}>
                Alle projecten →
              </Button>
            </Box>
            <Grid container spacing={2}>
              {projects.slice(0, 6).map(project => {
                const projectTasks = myTasks.filter(t => {
                  const taskProjects = t.fields?.ProjectNummer || [];
                  return taskProjects.includes(project.fields?.Projectnummer);
                });
                const completed = projectTasks.filter(t => t.fields?.Afgerond).length;

                return (
                  <Grid item xs={12} sm={6} md={4} key={project.id}>
                    <ProjectCard
                      project={project}
                      taskCount={projectTasks.length}
                      completedCount={completed}
                      projectStatus={projectStatuses?.[project.id]}
                      onClick={() => navigate(`/projects/${project.id}`)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ===== MICROSOFT TODO COMPONENT =====
function MicrosoftTodoSection({ msalInstance, account, onTodoCountChange }) {
  const [todos, setTodos] = useState([]);
  const [listInfo, setListInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [newTodoName, setNewTodoName] = useState('');
  const [creatingTodo, setCreatingTodo] = useState(false);

  const handleQuickAddTodo = async () => {
    const title = newTodoName.trim();
    if (!title || creatingTodo || !listInfo?.id) return;
    setCreatingTodo(true);
    try {
      const created = await createMsTodoTask(listInfo.id, { title });
      setTodos(prev => {
        const updated = [created, ...prev];
        onTodoCountChange?.(updated.length);
        return updated;
      });
      setNewTodoName('');
      toast.success('Taak aangemaakt in Microsoft To Do');
    } catch (err) {
      console.error('Quick add todo error:', err);
      toast.error('Fout bij aanmaken van taak in To Do');
    } finally {
      setCreatingTodo(false);
    }
  };

  const loadTodos = async () => {
    setLoading(true);
    setError(null);
    try {
      initMicrosoftTodoService(msalInstance, account);
      const result = await getMyTodos(false); // Only incomplete tasks
      setTodos(result.tasks);
      setListInfo({ name: result.listName, id: result.listId });
      onTodoCountChange?.(result.tasks.length);
    } catch (err) {
      console.error('Error loading Microsoft To Do tasks:', err);
      toast.error('Fout bij laden van Microsoft To Do taken');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (msalInstance && account) {
      loadTodos();
    }
  }, [msalInstance, account]);

  const handleComplete = async (taskId) => {
    if (!listInfo?.id || completingId) return;
    setCompletingId(taskId);
    try {
      await completeTask(listInfo.id, taskId);
      setTodos(prev => {
        const updated = prev.filter(t => t.id !== taskId);
        onTodoCountChange?.(updated.length);
        return updated;
      });
      biedOngedaanAan('To Do afgevinkt', async () => {
        await reopenTask(listInfo.id, taskId);
        await loadTodos();
      });
    } catch (err) {
      console.error('Error completing task:', err);
      toast.error('Fout bij voltooien van taak');
    } finally {
      setCompletingId(null);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TaskAltIcon sx={{ color: '#0078d4' }} />
          <Typography variant="subtitle1" fontWeight={700}>Microsoft To Do</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TaskAltIcon sx={{ color: '#0078d4' }} />
          <Typography variant="subtitle1" fontWeight={700}>Microsoft To Do</Typography>
          <IconButton size="small" onClick={loadTodos} sx={{ ml: 'auto' }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 2 }}>
          Kon taken niet laden. Klik op vernieuwen om opnieuw te proberen.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper data-section="microsoft-todo" sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TaskAltIcon sx={{ color: '#0078d4' }} />
        <Typography variant="subtitle1" fontWeight={700}>Microsoft To Do</Typography>
        <Chip
          label={listInfo?.name || 'Taken'}
          size="small"
          sx={{ ml: 1, backgroundColor: '#0078d420', color: '#0078d4', fontSize: '0.65rem' }}
        />
        <Chip label={todos.length} size="small" sx={{ ml: 'auto' }} />
        <IconButton size="small" onClick={loadTodos}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Quick add input */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Box
          component="input"
          placeholder="+ Nieuwe taak..."
          value={newTodoName}
          onChange={e => setNewTodoName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleQuickAddTodo()}
          disabled={creatingTodo}
          sx={{
            flex: 1, border: '1px solid #d1d5db', borderRadius: 1, px: 1.5, py: 1,
            fontSize: '0.875rem', outline: 'none', fontFamily: 'inherit',
            '&:focus': { borderColor: '#0078d4', boxShadow: '0 0 0 2px #0078d420' },
          }}
        />
        <IconButton size="small" onClick={handleQuickAddTodo} disabled={creatingTodo || !newTodoName.trim()}
          sx={{ color: '#0078d4', border: '1px solid #d1d5db', borderRadius: 1, px: 1 }}>
          {creatingTodo ? <CircularProgress size={18} /> : <AddIcon fontSize="small" />}
        </IconButton>
      </Box>

      {todos.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          🎉 Geen openstaande taken in Microsoft To Do!
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 300, overflowY: 'auto' }}>
          {todos.map(todo => {
            const isCompleting = completingId === todo.id;
            const dueDate = todoDueDate(todo) ? new Date(`${todoDueDate(todo)}T12:00:00`) : null;
            const isOverdue = dueDate && dueDate < new Date();
            const importance = todo.importance;
            
            return (
              <Paper
                key={todo.id}
                sx={{
                  p: 1.5,
                  borderLeft: `3px solid ${importance === 'high' ? '#ef4444' : '#0078d4'}`,
                  backgroundColor: isOverdue ? '#fef2f2' : '#fff',
                  opacity: isCompleting ? 0.6 : 1,
                  transition: 'all 0.15s',
                  '&:hover': { backgroundColor: '#f9fafb' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Tooltip title="Markeer als voltooid">
                    <Box
                      onClick={() => handleComplete(todo.id)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { transform: 'scale(1.2)' },
                        transition: 'transform 0.15s',
                      }}
                    >
                      {isCompleting ? (
                        <CircularProgress size={18} sx={{ mt: 0.25 }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#0078d4', mt: 0.25 }} />
                      )}
                    </Box>
                  </Tooltip>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: importance === 'high' ? 600 : 500,
                        color: '#111827',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {todo.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                      {importance === 'high' && (
                        <Chip
                          label="Belangrijk"
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem', backgroundColor: '#fee2e2', color: '#ef4444' }}
                        />
                      )}
                      {dueDate && (
                        <Typography variant="caption" color={isOverdue ? 'error' : 'text.secondary'}>
                          {isOverdue && <WarningIcon sx={{ fontSize: 10, mr: 0.25, verticalAlign: 'middle' }} />}
                          {dueDate.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}

// PL Own Tasks section with collapse
const MAX_PROJECTS_COLLAPSED = 3;
const MAX_TASKS_PER_PROJECT = 3;

function PLOwnTasksSection({ tasks, tasksByProject, onStatusUpdate, user, onTaskCreated }) {
  const [expanded, setExpanded] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleQuickAdd = async () => {
    const name = newTaskName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const today = formatDateISO(new Date());
      const response = await authFetch(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          NaamTaak: name,
          StartDateUitvoerder: today,
          EndDateUitvoerder: today,
          Assignee: [user?.id],
          PrioNr: 'Geen prio',
        }),
      });
      if (!response.ok) throw new Error('Fout bij aanmaken');
      const data = await response.json();
      setNewTaskName('');
      toast.success('Taak aangemaakt in Airtable');
      // Build a local task object with fields needed for dashboard filters
      if (onTaskCreated && data?.task) {
        const localTask = {
          id: data.task.id,
          fields: {
            ...data.task.fields,
            'Voornaam Assignee': [user?.voornaam || ''],
            'Email (Assignee)': [user?.email || ''],
          },
        };
        onTaskCreated(localTask);
      }
    } catch (err) {
      console.error('Quick add task error:', err);
      toast.error('Fout bij aanmaken van taak');
    } finally {
      setCreating(false);
    }
  };

  const visibleProjects = expanded ? tasksByProject : tasksByProject.slice(0, MAX_PROJECTS_COLLAPSED);
  const hiddenProjectCount = tasksByProject.length - MAX_PROJECTS_COLLAPSED;
  const hiddenTaskCount = expanded ? 0 : tasksByProject.slice(MAX_PROJECTS_COLLAPSED).reduce((sum, g) => sum + g.tasks.length, 0);

  return (
    <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <TaskAltIcon sx={{ color: '#3b82f6' }} />
        <Typography variant="subtitle1" fontWeight={700}>Mijn Taken (Airtable)</Typography>
        <Chip label={tasks.length} size="small" sx={{ ml: 'auto' }} />
      </Box>
      {/* Quick add input */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Box
          component="input"
          placeholder="+ Nieuwe taak..."
          value={newTaskName}
          onChange={e => setNewTaskName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleQuickAdd()}
          disabled={creating}
          sx={{
            flex: 1, border: '1px solid #d1d5db', borderRadius: 1, px: 1.5, py: 1,
            fontSize: '0.875rem', outline: 'none', fontFamily: 'inherit',
            '&:focus': { borderColor: '#3b82f6', boxShadow: '0 0 0 2px #3b82f620' },
          }}
        />
        <IconButton size="small" onClick={handleQuickAdd} disabled={creating || !newTaskName.trim()}
          sx={{ color: '#3b82f6', border: '1px solid #d1d5db', borderRadius: 1, px: 1 }}>
          {creating ? <CircularProgress size={18} /> : <AddIcon fontSize="small" />}
        </IconButton>
      </Box>
      {visibleProjects.map(group => {
        const visibleTasks = expanded ? group.tasks : group.tasks.slice(0, MAX_TASKS_PER_PROJECT);
        const hiddenInGroup = group.tasks.length - MAX_TASKS_PER_PROJECT;
        return (
          <Box key={group.projectNr} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <FolderIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                {group.projectNr !== 'Geen project' ? `${group.projectNr} — ${group.projectName}` : 'Geen project'}
              </Typography>
              <Chip label={group.tasks.length} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: 0.5 }}>
              {visibleTasks.map(task => (
                <TaskCard key={task.id} task={task} compact onStatusUpdate={onStatusUpdate} />
              ))}
              {!expanded && hiddenInGroup > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                  +{hiddenInGroup} meer taken
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
      {tasksByProject.length > MAX_PROJECTS_COLLAPSED && (
        <Button
          size="small"
          fullWidth
          onClick={() => setExpanded(!expanded)}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          {expanded ? 'Inklappen' : `Toon alles (+${hiddenProjectCount} projecten, ${hiddenTaskCount} taken)`}
        </Button>
      )}
    </Paper>
  );
}

// ===== PREP BRIEFING DIALOG =====
const CATERING_OPTIONS = ['Lunch', 'Koffie/thee', 'Diner', 'Snacks', 'Ontbijt'];

function PrepBriefingDialog({ draaidag, open, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  // Initialize form when dialog opens
  useEffect(() => {
    if (draaidag && open) {
      setForm({
        quitclaimsNodig: draaidag.quitclaimsNodig || null,
        quitclaimsHoeveel: draaidag.quitclaimsHoeveel || null,
        callsheetNodig: draaidag.callsheetNodig || null,
        printjes: draaidag.printjes || '',
        props: draaidag.props || '',
        catering: draaidag.catering || [],
        cateringAantal: draaidag.cateringAantal || null,
        bijzonderheden: draaidag.bijzonderheden || '',
        productieVooruit: draaidag.productieVooruit || false,
      });
    }
  }, [draaidag, open]);

  if (!draaidag) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/draaidagen/${draaidag.id}/prep`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error('Fout bij opslaan');
      toast.success('Voorbereiding opgeslagen');
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Prep save error:', err);
      toast.error('Fout bij opslaan voorbereiding');
    } finally {
      setSaving(false);
    }
  };

  const datumStr = draaidag.startDatum
    ? new Date(draaidag.startDatum).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })
    : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EditNoteIcon sx={{ color: '#3b82f6' }} />
          <Box>
            <Typography variant="h6" fontWeight={600}>Voorbereiding</Typography>
            <Typography variant="body2" color="text.secondary">
              {draaidag.naam || draaidag.projectnrEnNaam} — {datumStr}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
        {/* Quitclaims */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Quitclaims nodig?</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['Ja', 'Nee', 'Onbekend'].map(opt => (
              <Chip
                key={opt}
                label={opt}
                onClick={() => setForm(f => ({ ...f, quitclaimsNodig: opt }))}
                sx={{
                  cursor: 'pointer',
                  fontWeight: form.quitclaimsNodig === opt ? 700 : 400,
                  backgroundColor: form.quitclaimsNodig === opt
                    ? (opt === 'Ja' ? '#dcfce7' : opt === 'Nee' ? '#fee2e2' : '#f3f4f6')
                    : '#f9fafb',
                  color: form.quitclaimsNodig === opt
                    ? (opt === 'Ja' ? '#166534' : opt === 'Nee' ? '#991b1b' : '#374151')
                    : '#6b7280',
                  border: form.quitclaimsNodig === opt ? '2px solid currentColor' : '2px solid transparent',
                }}
              />
            ))}
          </Box>
          {form.quitclaimsNodig === 'Ja' && (
            <TextField
              size="small" type="number" label="Aantal quitclaims"
              value={form.quitclaimsHoeveel || ''}
              onChange={e => setForm(f => ({ ...f, quitclaimsHoeveel: parseInt(e.target.value, 10) || null }))}
              sx={{ mt: 1, width: 180 }}
            />
          )}
        </Box>

        {/* Callsheet */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Callsheet nodig?</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['Ja', 'Nee'].map(opt => (
              <Chip
                key={opt}
                label={opt}
                onClick={() => setForm(f => ({ ...f, callsheetNodig: opt }))}
                sx={{
                  cursor: 'pointer',
                  fontWeight: form.callsheetNodig === opt ? 700 : 400,
                  backgroundColor: form.callsheetNodig === opt
                    ? (opt === 'Ja' ? '#dcfce7' : '#fee2e2')
                    : '#f9fafb',
                  color: form.callsheetNodig === opt
                    ? (opt === 'Ja' ? '#166534' : '#991b1b')
                    : '#6b7280',
                  border: form.callsheetNodig === opt ? '2px solid currentColor' : '2px solid transparent',
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Printjes */}
        <TextField
          label="Printjes nodig?"
          placeholder="Bijv. Script v3, moodboard, storyboard..."
          multiline rows={2} fullWidth size="small"
          value={form.printjes}
          onChange={e => setForm(f => ({ ...f, printjes: e.target.value }))}
        />

        {/* Props */}
        <TextField
          label="Props"
          placeholder="Bijv. 2x laptop, koffiebekers, kleding..."
          multiline rows={2} fullWidth size="small"
          value={form.props}
          onChange={e => setForm(f => ({ ...f, props: e.target.value }))}
        />

        {/* Catering */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Catering</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            {CATERING_OPTIONS.map(opt => (
              <Chip
                key={opt}
                label={opt}
                onClick={() => setForm(f => ({
                  ...f,
                  catering: f.catering.includes(opt)
                    ? f.catering.filter(c => c !== opt)
                    : [...f.catering, opt]
                }))}
                sx={{
                  cursor: 'pointer',
                  fontWeight: form.catering?.includes(opt) ? 700 : 400,
                  backgroundColor: form.catering?.includes(opt) ? '#dbeafe' : '#f9fafb',
                  color: form.catering?.includes(opt) ? '#1e40af' : '#6b7280',
                  border: form.catering?.includes(opt) ? '2px solid #3b82f6' : '2px solid transparent',
                }}
              />
            ))}
          </Box>
          {form.catering?.length > 0 && (
            <TextField
              size="small" type="number" label="Aantal personen"
              value={form.cateringAantal || ''}
              onChange={e => setForm(f => ({ ...f, cateringAantal: parseInt(e.target.value, 10) || null }))}
              sx={{ width: 180 }}
            />
          )}
        </Box>

        {/* Bijzonderheden */}
        <TextField
          label="Bijzonderheden"
          placeholder="Bijv. Parkeervergunning, klant komt om 14:00, locatie-specifieke info..."
          multiline rows={3} fullWidth size="small"
          value={form.bijzonderheden}
          onChange={e => setForm(f => ({ ...f, bijzonderheden: e.target.value }))}
        />

        {/* PL Akkoord */}
        <FormControlLabel
          control={
            <Checkbox
              checked={form.productieVooruit}
              onChange={e => setForm(f => ({ ...f, productieVooruit: e.target.checked }))}
              sx={{ color: '#10b981', '&.Mui-checked': { color: '#10b981' } }}
            />
          }
          label={
            <Typography variant="body2" fontWeight={form.productieVooruit ? 700 : 400}>
              Briefing compleet — klaar voor productie-assistent
            </Typography>
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Annuleren</Button>
        <Button
          variant="contained" onClick={handleSave} disabled={saving}
          sx={{ backgroundColor: '#3b82f6', '&:hover': { backgroundColor: '#2563eb' } }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Opslaan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===== DRAAIDAGEN TE BRIEFEN (PL section) =====
function DraaidagenTeBriefen({ draaidagen, userNaam, onRefresh }) {
  const [briefingDraaidag, setBriefingDraaidag] = useState(null);

  // Filter: draaidagen from PL's projects that need prep
  const plDraaidagen = useMemo(() => {
    if (!userNaam) return [];
    return draaidagen.filter(d => {
      // Check if user is PL for this draaidag's project
      const plNames = d.plFromProject || [];
      const isPL = plNames.some(pl => pl?.toLowerCase().includes(userNaam.toLowerCase()));
      if (!isPL) return false;
      // Only show if prep is not complete
      return !d.geenPreproductie && !d.preproductieVoorbereid;
    });
  }, [draaidagen, userNaam]);

  if (plDraaidagen.length === 0) return null;

  const getPrepStatusConfig = (d) => {
    if (d.productieVooruit) return { label: 'Ingevuld', color: '#a16207', bg: '#fef3c7', icon: '🟡' };
    // Check if any field is filled
    const hasAnyPrep = d.quitclaimsNodig || d.callsheetNodig || d.printjes || d.props || d.catering?.length > 0 || d.bijzonderheden;
    if (hasAnyPrep) return { label: 'Deels ingevuld', color: '#a16207', bg: '#fef3c7', icon: '🟨' };
    return { label: 'Niet ingevuld', color: '#dc2626', bg: '#fee2e2', icon: '🟥' };
  };

  return (
    <>
      <PrepBriefingDialog
        draaidag={briefingDraaidag}
        open={!!briefingDraaidag}
        onClose={() => setBriefingDraaidag(null)}
        onSaved={onRefresh}
      />
      <Paper sx={{ p: 2, border: '2px solid #3b82f6', borderRadius: 2, backgroundColor: '#eff6ff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <EditNoteIcon sx={{ color: '#3b82f6' }} />
          <Typography variant="subtitle1" fontWeight={700}>Draaidagen te Briefen</Typography>
          <Chip label={plDraaidagen.length} size="small" sx={{ ml: 'auto', backgroundColor: '#dbeafe', color: '#1e40af' }} />
        </Box>
        <Grid container spacing={1.5}>
          {plDraaidagen.map(d => {
            const statusCfg = getPrepStatusConfig(d);
            const datumStr = d.startDatum
              ? new Date(d.startDatum).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
              : '';
            // Kalenderdagen in NL-tijd: om 00:30 werd een draaidag van vandaag anders 'Morgen'.
            const daysUntil = d.startDatum ? dagenTot(d.startDatum) : null;

            return (
              <Grid item xs={12} sm={6} md={4} key={d.id}>
                <Paper
                  onClick={() => setBriefingDraaidag(d)}
                  sx={{
                    p: 2, cursor: 'pointer', border: '1px solid #e5e7eb',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: '#3b82f6', boxShadow: '0 2px 8px rgba(59,130,246,0.15)' },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1, mr: 1 }}>
                      {datumStr} — {d.naam || d.projectnaam || 'Draaidag'}
                    </Typography>
                    {daysUntil !== null && daysUntil <= 7 && (
                      <Chip
                        label={daysUntil === 0 ? 'Vandaag' : daysUntil === 1 ? 'Morgen' : `${daysUntil}d`}
                        size="small"
                        sx={{
                          height: 18, fontSize: '0.6rem',
                          backgroundColor: daysUntil <= 2 ? '#fee2e2' : '#fef3c7',
                          color: daysUntil <= 2 ? '#dc2626' : '#a16207',
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {d.projectnrEnNaam || d.projectnaam || ''}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Chip
                      label={`${statusCfg.icon} ${statusCfg.label}`}
                      size="small"
                      sx={{ height: 20, fontSize: '0.6rem', backgroundColor: statusCfg.bg, color: statusCfg.color }}
                    />
                    <Typography variant="caption" color="primary" sx={{ ml: 'auto', fontWeight: 600 }}>
                      Brief invullen →
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </>
  );
}

// ===== VOORBEREIDING CHECKLIST (Producer section) =====
function VoorbereidingChecklist({ draaidagen }) {
  const [expandedId, setExpandedId] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});

  // Filter: draaidagen with any prep filled in
  const prepDraaidagen = useMemo(() => {
    return draaidagen.filter(d => {
      return d.productieVooruit || d.quitclaimsNodig || d.callsheetNodig || d.printjes || d.props || d.catering?.length > 0 || d.bijzonderheden;
    });
  }, [draaidagen]);

  if (prepDraaidagen.length === 0) return null;

  const toggleCheck = (draaidagId, item) => {
    setCheckedItems(prev => ({
      ...prev,
      [`${draaidagId}-${item}`]: !prev[`${draaidagId}-${item}`],
    }));
  };

  const getChecklistItems = (d) => {
    const items = [];
    if (d.callsheetNodig === 'Ja') {
      items.push({ key: 'callsheet', label: 'Callsheet maken', done: d.aantalCallsheets > 0 });
    }
    if (d.quitclaimsNodig === 'Ja') {
      const qcLabel = d.quitclaimsHoeveel ? `Quitclaims regelen (${d.quitclaimsHoeveel}x)` : 'Quitclaims regelen';
      items.push({ key: 'quitclaims', label: qcLabel, done: d.quitclaimsStatus === 'Getekend' });
    }
    if (d.printjes) items.push({ key: 'printjes', label: `Printjes: ${d.printjes}` });
    if (d.props) items.push({ key: 'props', label: `Props: ${d.props}` });
    if (d.catering?.length > 0) {
      const cateringLabel = d.cateringAantal
        ? `Catering: ${d.catering.join(', ')} (${d.cateringAantal} pers.)`
        : `Catering: ${d.catering.join(', ')}`;
      items.push({ key: 'catering', label: cateringLabel });
    }
    if (d.bijzonderheden) items.push({ key: 'bijzonderheden', label: `Bijzonderheden: ${d.bijzonderheden}` });
    return items;
  };

  return (
    <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PlaylistAddCheckIcon sx={{ color: '#f59e0b' }} />
        <Typography variant="subtitle1" fontWeight={700}>Voorbereiding per Draaidag</Typography>
        <Chip label={prepDraaidagen.length} size="small" sx={{ ml: 'auto' }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {prepDraaidagen.map(d => {
          const items = getChecklistItems(d);
          const isExpanded = expandedId === d.id;
          const datumStr = d.startDatum
            ? new Date(d.startDatum).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })
            : '';
          const checkedCount = items.filter(item =>
            item.done || checkedItems[`${d.id}-${item.key}`]
          ).length;

          return (
            <Paper key={d.id} variant="outlined" sx={{ overflow: 'hidden' }}>
              <Box
                onClick={() => setExpandedId(isExpanded ? null : d.id)}
                sx={{
                  p: 1.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                  backgroundColor: checkedCount === items.length && items.length > 0 ? '#f0fdf4' : '#fff',
                  '&:hover': { backgroundColor: '#f9fafb' },
                }}
              >
                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {datumStr} — {d.projectnrEnNaam || d.naam}
                  </Typography>
                </Box>
                <Chip
                  label={`${checkedCount}/${items.length}`}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.65rem',
                    backgroundColor: checkedCount === items.length && items.length > 0 ? '#dcfce7' : '#f3f4f6',
                    color: checkedCount === items.length && items.length > 0 ? '#166534' : '#6b7280',
                  }}
                />
              </Box>
              <Collapse in={isExpanded}>
                <Box sx={{ px: 2, pb: 1.5 }}>
                  {items.map(item => {
                    const isChecked = item.done || checkedItems[`${d.id}-${item.key}`];
                    return (
                      <Box
                        key={item.key}
                        onClick={() => !item.done && toggleCheck(d.id, item.key)}
                        sx={{
                          display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.75,
                          cursor: item.done ? 'default' : 'pointer',
                          '&:hover': !item.done ? { backgroundColor: '#f9fafb' } : {},
                          borderRadius: 1, px: 0.5,
                        }}
                      >
                        {isChecked ? (
                          <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981', mt: 0.25 }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#d1d5db', mt: 0.25 }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            textDecoration: isChecked ? 'line-through' : 'none',
                            color: isChecked ? '#9ca3af' : '#374151',
                            flex: 1,
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    );
                  })}
                  {!d.productieVooruit && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                      PL heeft briefing nog niet afgerond
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
}

// ===== AANKOMENDE DRAAIDAGEN (Uitvoerder/Crew section) =====
function AankomendeDraaidagen({ draaidagen, userEmail, userNaam }) {
  const navigate = useNavigate();

  // Filter: draaidagen where user is in crew
  const myDraaidagen = useMemo(() => {
    if (!userEmail && !userNaam) return [];
    return draaidagen.filter(d => {
      const crewEmails = d.crewEmail || [];
      const crewNames = d.crewNaam || [];
      const emailMatch = userEmail && crewEmails.some(e => e?.toLowerCase() === userEmail.toLowerCase());
      const nameMatch = userNaam && crewNames.some(n => n?.toLowerCase().includes(userNaam.toLowerCase()));
      return emailMatch || nameMatch;
    });
  }, [draaidagen, userEmail, userNaam]);

  if (myDraaidagen.length === 0) return null;

  return (
    <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <VideocamIcon sx={{ color: '#f59e0b' }} />
        <Typography variant="subtitle1" fontWeight={700}>Aankomende Draaidagen</Typography>
        <Chip label={myDraaidagen.length} size="small" sx={{ ml: 'auto' }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {myDraaidagen.slice(0, 5).map(d => {
          const datumStr = d.startDatum
            ? new Date(d.startDatum).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })
            : '';
          // Kalenderdagen in NL-tijd: om 00:30 werd een draaidag van vandaag anders 'Morgen'.
          const daysUntil = d.startDatum ? dagenTot(d.startDatum) : null;

          return (
            <Paper key={d.id} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {datumStr} — {d.projectnrEnNaam || d.naam}
                </Typography>
                {daysUntil !== null && daysUntil <= 3 && (
                  <Chip
                    label={daysUntil === 0 ? 'Vandaag!' : daysUntil === 1 ? 'Morgen' : `Over ${daysUntil}d`}
                    size="small"
                    sx={{
                      height: 18, fontSize: '0.6rem',
                      backgroundColor: daysUntil <= 1 ? '#fee2e2' : '#fef3c7',
                      color: daysUntil <= 1 ? '#dc2626' : '#a16207',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {d.locatie && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <LocationOnIcon sx={{ fontSize: 12 }} /> {d.locatie}
                  </Typography>
                )}
                {d.crewNaam?.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {d.crewNaam.length} crew
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  size="small" variant="outlined"
                  startIcon={<CameraAltIcon sx={{ fontSize: 14 }} />}
                  onClick={() => navigate('/MaterialReservation')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                >
                  Materiaal
                </Button>
                {d.callsheet?.length > 0 && (
                  <Button
                    size="small" variant="outlined"
                    startIcon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                    onClick={() => navigate(`/callsheets/${d.callsheet[0]}`)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25 }}
                  >
                    Callsheet
                  </Button>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
}

// ===== PL DASHBOARD =====
function PLDashboard({ user, tasks, projects, projectStatuses, onStatusUpdate, msalInstance, account, meetings, onTaskCreated, draaidagen, onRefreshDraaidagen, deadlines }) {
  const navigate = useNavigate();
  const [todoCount, setTodoCount] = useState(null);
  const [dlAnchor, setDlAnchor] = useState(null);
  const [selectedDl, setSelectedDl] = useState(null);

  // PL sees all tasks for their projects
  const plProjects = projects.filter(p => {
    const pls = p.fields?.['Projectleider (Volledige naam)'] || [];
    return pls.some(pl => pl?.toLowerCase().includes(user?.naam?.toLowerCase()));
  });

  // Get project numbers for filtering
  const plProjectNumbers = plProjects.map(p => p.fields?.Projectnummer).filter(Boolean);

  // Group projects by phase/status
  const projectsByStatus = useMemo(() => {
    const groups = {
      'In progress': [],
      'On hold': [],
      'Nog niet gestart': [],
    };
    plProjects.forEach(p => {
      const status = p.fields?.Status || 'In progress';
      if (groups[status]) groups[status].push(p);
      else groups['In progress'].push(p);
    });
    return groups;
  }, [plProjects]);

  // Tasks that are "Afgerond" and need to be sent by PL (only from PL's projects)
  const tasksToSend = tasks.filter(t => {
    const prioNr = t.fields?.PrioNr;
    const type = t.fields?.Type || '';
    const taskProjectNr = t.fields?.ProjectNummer?.[0];
    if (prioNr !== 'Afgerond') return false;
    if (type === 'Vanallesuurtje') return false;
    // Only show if it's from one of PL's projects
    return !taskProjectNr || plProjectNumbers.includes(taskProjectNr);
  });

  // Tasks that need attention (overdue or due soon) - ONLY from PL's projects
  const urgentTasks = tasks.filter(t => {
    const deadline = t.fields?.Deadline;
    const prioNr = t.fields?.PrioNr;
    const taskProjectNr = t.fields?.ProjectNummer?.[0];
    if (!deadline || AFGEROND_SET.has(prioNr)) return false;
    // Een voorbije draaidag of vrije dag vraagt geen aandacht meer (3 nov 2026).
    if (isDagstaatTaak(t) && new Date(deadline) < new Date(new Date().toDateString())) return false;
    // Only show if it's from one of PL's projects
    if (taskProjectNr && !plProjectNumbers.includes(taskProjectNr)) return false;
    const d = new Date(deadline);
    const today = new Date();
    const diff = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
    return diff <= 3;
  });

  // Meetings that need to be processed (AI Engine format: m.status)
  const pendingMeetings = (meetings || []).filter(m => {
    const status = m.status || '';
    return status === 'new' || status === 'processing' || status === 'review_needed';
  });

  // PL's own tasks: tasks where the PL is the assignee (not just overseer)
  // Afspraken only shown if they are today
  const plOwnTasks = useMemo(() => {
    if (!user?.voornaam) return [];
    return tasks.filter(t => {
      const assignees = t.fields?.['Voornaam Assignee'] || [];
      const prioNr = t.fields?.PrioNr || '';
      const type = t.fields?.Type || '';
      if (AFGEROND_SET.has(prioNr)) return false;
      if (!assignees.some(a => a?.toLowerCase().includes(user.voornaam?.toLowerCase()))) return false;
      // Afspraken: only show today's
      if (prioNr === 'Afspraak' || type === 'Afspraak') {
        return isToday(t.fields?.StartDateUitvoerder);
      }
      // Filter out vrije dagen, vanallesuurtjes, intern
      if (prioNr === 'Vrij/intern' || type === 'Vrije dag' || type === 'Intern' || type === 'Vanallesuurtje') {
        return false;
      }
      return true;
    });
  }, [tasks, user]);

  // Map project numbers to names
  const projectNameMap = useMemo(() => {
    const map = {};
    plProjects.forEach(p => {
      const nr = p.fields?.Projectnummer;
      if (nr) map[nr] = p.fields?.Projectnaam || p.fields?.Name || nr;
    });
    return map;
  }, [plProjects]);

  // Group PL's own tasks by project
  const plOwnTasksByProject = useMemo(() => {
    const groups = {};
    plOwnTasks.forEach(t => {
      const projectNr = t.fields?.ProjectNummer?.[0] || 'Geen project';
      const projectName = projectNameMap[projectNr] || projectNr;
      const key = projectNr;
      if (!groups[key]) groups[key] = { projectNr, projectName, tasks: [] };
      groups[key].tasks.push(t);
    });
    return Object.values(groups).sort((a, b) => b.tasks.length - a.tasks.length);
  }, [plOwnTasks, projectNameMap]);

  // Filter deadlines: only external, deliverable-linked tasks + deliverables
  const filteredDeadlines = useMemo(() => {
    const isInternal = (name) => {
      if (!name) return false;
      return name.replace(/^\d+\.\s*/, '').startsWith('Full Frame');
    };
    return (deadlines || []).filter(d => {
      if (isInternal(d.projectName)) return false;
      if (d.type === 'task' && !d.deliverableId) return false;
      return true;
    });
  }, [deadlines]);

  return (
    <Box>
      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2}>
          <Paper onClick={() => navigate('/projects')} sx={{ p: 2, textAlign: 'center', backgroundColor: '#dbeafe', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
            <Typography variant="h4" fontWeight={700} color="primary">{plProjects.length}</Typography>
            <Typography variant="caption" color="text.secondary">Projecten</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper onClick={() => navigate('/dashboard')} sx={{ p: 2, textAlign: 'center', backgroundColor: '#fef3c7', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#f59e0b' }}>{urgentTasks.length}</Typography>
            <Typography variant="caption" color="text.secondary">Urgente taken</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper onClick={() => navigate('/projects')} sx={{ p: 2, textAlign: 'center', backgroundColor: '#dcfce7', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#10b981' }}>{projectsByStatus['In progress'].length}</Typography>
            <Typography variant="caption" color="text.secondary">In uitvoering</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper onClick={() => navigate('/projects')} sx={{ p: 2, textAlign: 'center', backgroundColor: '#f3f4f6', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#6b7280' }}>{projectsByStatus['On hold'].length}</Typography>
            <Typography variant="caption" color="text.secondary">On hold</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper onClick={() => {
            const todoSection = document.querySelector('[data-section="microsoft-todo"]');
            if (todoSection) todoSection.scrollIntoView({ behavior: 'smooth' });
          }} sx={{ p: 2, textAlign: 'center', backgroundColor: '#ede9fe', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#8b5cf6' }}>{todoCount ?? '—'}</Typography>
            <Typography variant="caption" color="text.secondary">To Do</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={2}>
          <Paper onClick={() => navigate('/MeetingDashboard')} sx={{ p: 2, textAlign: 'center', backgroundColor: pendingMeetings.length > 0 ? '#fef3c7' : '#f3f4f6', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: pendingMeetings.length > 0 ? '#f59e0b' : '#6b7280' }}>{pendingMeetings.length}</Typography>
            <Typography variant="caption" color="text.secondary">Meetings</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Links */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
          SNELLE LINKS
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <QuickLinkCard
              title="Meeting Notities"
              description="AI-gegenereerde notities"
              icon={AssignmentIcon}
              to="/MeetingDashboard"
              color="#8b5cf6"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <QuickLinkCard
              title="Alle Projecten"
              description="Project overzicht"
              icon={FolderIcon}
              to="/projects"
              color="#3b82f6"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <QuickLinkCard
              title="Ondertitels"
              description="SRT Generator"
              icon={SubtitlesIcon}
              to="/SRTGenerator"
              color="#10b981"
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Row 1: Urgent Tasks (if any, full width at top) */}
        {urgentTasks.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, border: '2px solid #fbbf24', borderRadius: 2, backgroundColor: '#fffbeb' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WarningIcon sx={{ color: '#f59e0b' }} />
                <Typography variant="subtitle1" fontWeight={700}>Urgente Taken</Typography>
                <Chip label={urgentTasks.length} size="small" sx={{ ml: 'auto' }} />
              </Box>
              <Grid container spacing={1}>
                {urgentTasks.slice(0, 6).map(task => (
                  <Grid item xs={12} sm={6} md={4} key={task.id}>
                    <TaskCard task={task} compact onStatusUpdate={onStatusUpdate} showProjectName showAssignee />
                  </Grid>
                ))}
              </Grid>
              {urgentTasks.length > 6 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  +{urgentTasks.length - 6} meer urgente taken
                </Typography>
              )}
            </Paper>
          </Grid>
        )}

        {/* Draaidagen te Briefen (PL prep) */}
        {draaidagen?.length > 0 && (
          <Grid item xs={12}>
            <DraaidagenTeBriefen draaidagen={draaidagen} userNaam={user?.naam} onRefresh={onRefreshDraaidagen} />
          </Grid>
        )}

        {/* Opleveringen deze week */}
        {filteredDeadlines.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, border: '1px solid #3b82f6', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <AssignmentIcon sx={{ color: '#3b82f6' }} />
                <Typography variant="subtitle1" fontWeight={700}>Opleveringen deze week</Typography>
                <Chip label={filteredDeadlines.length} size="small" sx={{ ml: 'auto', backgroundColor: '#dbeafe', color: '#1d4ed8' }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {filteredDeadlines.map((d, i) => {
                  const isDeliverable = d.type === 'deliverable';
                  const dateStr = d.deadline ? new Date(d.deadline).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
                  const isDueToday = d.daysUntil === 0;
                  const isPast = d.daysUntil < 0;
                  return (
                    <Box key={`${d.id}-${i}`} onClick={(e) => { setDlAnchor(e.currentTarget); setSelectedDl(d); }} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1,
                      borderRadius: 1.5, backgroundColor: isDeliverable ? '#fff7ed' : '#f8fafc',
                      border: `1px solid ${isDeliverable ? '#fdba74' : (isDueToday || isPast) ? '#f87171' : '#e2e8f0'}`,
                      cursor: 'pointer', '&:hover': { opacity: 0.85 },
                    }}>
                      <Typography sx={{ fontSize: '0.85rem' }}>{isDeliverable ? '🎯' : '📦'}</Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1f2937', lineHeight: 1.3 }}>
                          {d.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', lineHeight: 1.2 }}>
                          {d.projectName?.replace(/^\d+\.\s*/, '')}
                        </Typography>
                      </Box>
                      <Chip
                        label={dateStr}
                        size="small"
                        sx={{
                          fontSize: '0.65rem', height: 22, flexShrink: 0,
                          backgroundColor: (isDueToday || isPast) ? '#fef2f2' : '#f1f5f9',
                          color: (isDueToday || isPast) ? '#dc2626' : '#64748b',
                          fontWeight: (isDueToday || isPast) ? 700 : 500,
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Deadline popover */}
        <Popover
          open={Boolean(dlAnchor)}
          anchorEl={dlAnchor}
          onClose={() => { setDlAnchor(null); setSelectedDl(null); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { p: 2, maxWidth: 340, borderRadius: 2 } } }}
        >
          {selectedDl && (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>
                {selectedDl.type === 'deliverable' ? '🎯 ' : '📦 '}{selectedDl.name}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 0.5 }}>
                {selectedDl.projectNumber ? `${selectedDl.projectNumber}. ` : ''}{selectedDl.projectName?.replace(/^\d+\.\s*/, '')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Chip label={new Date(selectedDl.deadline).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })} size="small" sx={{ fontSize: '0.7rem' }} />
                {selectedDl.deadlineType && <Chip label={selectedDl.deadlineType} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
              </Box>
              {selectedDl.type === 'task' && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af', mb: 0.5 }}>Status</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {PRIO_OPTIONS.filter(o => ['Prio 1','Prio 2','Prio 3','Geen prio','Wacht op feedback','Afgerond','Opgestuurd'].includes(o.value)).map(o => (
                      <Chip
                        key={o.value}
                        label={o.label}
                        size="small"
                        variant={selectedDl.status === o.value ? 'filled' : 'outlined'}
                        sx={{
                          fontSize: '0.65rem', height: 22, cursor: 'pointer',
                          ...(selectedDl.status === o.value ? { bgcolor: o.color, color: '#fff', borderColor: o.color } : { borderColor: '#e5e7eb' }),
                        }}
                        onClick={async () => {
                          try {
                            await authFetch(`${BACKEND_URL}/Api/tasks/${selectedDl.id}`, {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ fields: { PrioNr: o.value } }),
                            });
                            setSelectedDl({ ...selectedDl, status: o.value });
                            toast.success(`Status → ${o.value}`);
                          } catch { toast.error('Status wijzigen mislukt'); }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              <Button
                size="small"
                variant="contained"
                fullWidth
                onClick={() => { navigate(`/projects/${selectedDl.projectId}`); setDlAnchor(null); }}
                sx={{ textTransform: 'none', fontSize: '0.8rem' }}
              >
                Ga naar project →
              </Button>
            </Box>
          )}
        </Popover>

        {/* Nieuwe projecten: één klik naar de opstart-wizard (koppelingen checken,
            debrief, slimme taken, fasen) — de initialisatie hoort hier te beginnen,
            niet pas als iemand toevallig de flow opent. */}
        {projectsByStatus['Nog niet gestart'].length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, border: '1px solid #e9d5ff', borderRadius: 2, backgroundColor: '#faf5ff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography sx={{ fontSize: 18, lineHeight: 1 }}>🚀</Typography>
                <Typography variant="subtitle1" fontWeight={700}>Nieuwe projecten — nog opstarten</Typography>
                <Chip label={projectsByStatus['Nog niet gestart'].length} size="small" sx={{ ml: 'auto' }} />
              </Box>
              <Grid container spacing={1.5}>
                {projectsByStatus['Nog niet gestart'].slice(0, 6).map(project => (
                  <Grid item xs={12} sm={6} md={4} key={project.id}>
                    <Paper sx={{ p: 1.5, border: '1px solid #e5e7eb', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                          {project.fields?.Projectnr_en_naam || project.fields?.Projectnaam || 'Project'}
                        </Typography>
                        {project.fields?.KlantnaamString && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {project.fields.KlantnaamString}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => navigate(`/projects/${project.id}?weergave=flow&opstart=1`)}
                        sx={{ textTransform: 'none', fontSize: '0.75rem', flexShrink: 0, whiteSpace: 'nowrap' }}
                      >
                        🚀 Opstarten
                      </Button>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Row 2: Active Projects (always visible, left) + Microsoft To Do (always visible, right) */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <WorkIcon sx={{ color: '#3b82f6' }} />
              <Typography variant="subtitle1" fontWeight={700}>Actieve Projecten</Typography>
              <Chip label={projectsByStatus['In progress'].length} size="small" sx={{ ml: 'auto' }} />
              <Button size="small" onClick={() => navigate('/projects')}>
                Alle →
              </Button>
            </Box>
            <Grid container spacing={1.5}>
              {projectsByStatus['In progress'].slice(0, 6).map(project => (
                <Grid item xs={12} sm={6} key={project.id}>
                  <ProjectCard
                    project={project}
                    taskCount={0}
                    completedCount={0}
                    projectStatus={projectStatuses?.[project.id]}
                    onClick={() => navigate(`/projects/${project.id}`)}
                  />
                </Grid>
              ))}
            </Grid>
            {projectsByStatus['In progress'].length > 6 && (
              <Button fullWidth sx={{ mt: 2 }} onClick={() => navigate('/projects')}>
                Alle {projectsByStatus['In progress'].length} projecten bekijken
              </Button>
            )}
          </Paper>
        </Grid>

        {/* Microsoft To Do - Always visible on right */}
        <Grid item xs={12} md={6}>
          <MicrosoftTodoSection msalInstance={msalInstance} account={account} onTodoCountChange={setTodoCount} />
        </Grid>

        {/* Mijn Taken (Airtable) - half width */}
        {plOwnTasks.length > 0 && (
          <Grid item xs={12} md={6}>
            <PLOwnTasksSection tasks={plOwnTasks} tasksByProject={plOwnTasksByProject} onStatusUpdate={onStatusUpdate} user={user} onTaskCreated={onTaskCreated} />
          </Grid>
        )}

        {/* Row 3: Tasks to Send (conditional) */}
        {tasksToSend.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, border: '2px solid #10b981', borderRadius: 2, backgroundColor: '#f0fdf4' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PlaylistAddCheckIcon sx={{ color: '#10b981' }} />
                <Typography variant="subtitle1" fontWeight={700}>Te Versturen</Typography>
                <Chip label={tasksToSend.length} size="small" sx={{ ml: 'auto', backgroundColor: '#dcfce7', color: '#166534' }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Deze taken zijn afgerond door uitvoerders en kunnen nu opgestuurd worden naar de klant.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {tasksToSend.slice(0, 5).map(task => (
                  <TaskCard key={task.id} task={task} compact onStatusUpdate={onStatusUpdate} showProjectName />
                ))}
                {tasksToSend.length > 5 && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                    +{tasksToSend.length - 5} meer taken te versturen
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Row 4: Meetings to Process (conditional) */}
        {pendingMeetings.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EventIcon sx={{ color: '#8b5cf6' }} />
                <Typography variant="subtitle1" fontWeight={700}>Meetings te Verwerken</Typography>
                <Chip label={pendingMeetings.length} size="small" sx={{ ml: 'auto', backgroundColor: '#ede9fe', color: '#7c3aed' }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {pendingMeetings.slice(0, 5).map(meeting => {
                  const title = meeting.title || 'Meeting';
                  const date = meeting.meeting_date || meeting.date;
                  const participants = meeting.participants || [];
                  
                  return (
                    <Paper
                      key={meeting.id}
                      onClick={() => navigate('/MeetingDashboard')}
                      sx={{
                        p: 1.5,
                        borderLeft: '3px solid #8b5cf6',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f9fafb' },
                      }}
                    >
                      <Typography variant="body2" fontWeight={500} noWrap>{title}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        {date && (
                          <Typography variant="caption" color="text.secondary">
                            📅 {formatDate(date)}
                          </Typography>
                        )}
                        {participants.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            👥 {participants.length} deelnemers
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
                {pendingMeetings.length > 5 && (
                  <Button size="small" onClick={() => navigate('/MeetingDashboard')}>
                    Alle {pendingMeetings.length} meetings bekijken →
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

// ===== PRODUCER DASHBOARD =====
function ProducerDashboard({ user, tasks, projects, projectStatuses, callsheets, quitclaims, onStatusUpdate, draaidagen }) {
  const navigate = useNavigate();
  
  // Callsheets that need attention
  const pendingCallsheets = callsheets?.filter(cs => {
    const status = cs.fields?.['Status PDF'] || '';
    return !status.includes('gereed');
  }) || [];

  // Quitclaims that need attention
  const pendingQuitclaims = quitclaims?.filter(qc => {
    const status = qc.fields?.Status || '';
    return !status.includes('Getekend') && !status.includes('Niet nodig');
  }) || [];

  return (
    <Box>
      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#dbeafe' }}>
            <Typography variant="h4" fontWeight={700} color="primary">{projects.length}</Typography>
            <Typography variant="caption" color="text.secondary">Projecten</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fef3c7' }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#f59e0b' }}>{pendingCallsheets.length}</Typography>
            <Typography variant="caption" color="text.secondary">Callsheets nodig</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fee2e2' }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#ef4444' }}>{pendingQuitclaims.length}</Typography>
            <Typography variant="caption" color="text.secondary">Quitclaims nodig</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#dcfce7' }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#10b981' }}>{tasks.length}</Typography>
            <Typography variant="caption" color="text.secondary">Taken</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Links */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
          PRODUCTIE TOOLS
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <QuickLinkCard
              title="Materiaal Reserveren"
              description="Camera's en apparatuur"
              icon={CameraAltIcon}
              to="/MaterialReservation"
              color="#3b82f6"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <QuickLinkCard
              title="Alle Projecten"
              description="Project overzicht"
              icon={FolderIcon}
              to="/projects"
              color="#10b981"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <QuickLinkCard
              title="Meeting Notities"
              description="Vergaderverslagen"
              icon={AssignmentIcon}
              to="/MeetingDashboard"
              color="#8b5cf6"
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Pending Callsheets */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PhoneIcon sx={{ color: '#f59e0b' }} />
              <Typography variant="subtitle1" fontWeight={700}>Callsheets Te Maken</Typography>
              <Chip label={pendingCallsheets.length} size="small" color="warning" sx={{ ml: 'auto' }} />
            </Box>
            {pendingCallsheets.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                ✓ Alle callsheets zijn klaar!
              </Typography>
            ) : (
              <List dense>
                {pendingCallsheets.slice(0, 5).map(cs => (
                  <ListItem key={cs.id}>
                    <ListItemIcon><PhoneIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                    <ListItemText
                      primary={cs.fields?.Titel || 'Callsheet'}
                      secondary={formatDate(cs.fields?.['Datum (handmatig)'])}
                    />
                    <Chip label={cs.fields?.['Status PDF'] || 'Te maken'} size="small" />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Pending Quitclaims */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <DescriptionIcon sx={{ color: '#ef4444' }} />
              <Typography variant="subtitle1" fontWeight={700}>Quitclaims Nodig</Typography>
              <Chip label={pendingQuitclaims.length} size="small" color="error" sx={{ ml: 'auto' }} />
            </Box>
            {pendingQuitclaims.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                ✓ Alle quitclaims zijn geregeld!
              </Typography>
            ) : (
              <List dense>
                {pendingQuitclaims.slice(0, 5).map(qc => (
                  <ListItem key={qc.id}>
                    <ListItemIcon><PersonIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                    <ListItemText
                      primary={qc.fields?.Naam || 'Quitclaim'}
                      secondary={qc.fields?.Status || 'Te regelen'}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Voorbereiding per Draaidag */}
        {draaidagen?.length > 0 && (
          <Grid item xs={12}>
            <VoorbereidingChecklist draaidagen={draaidagen} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

// ===== CREATIVE DASHBOARD =====
function CreativeDashboard({ user, tasks, projects, projectStatuses, onStatusUpdate }) {
  const navigate = useNavigate();
  
  // Filter tasks for creative reviews (exclude completed ones)
  const reviewTasks = tasks.filter(t => {
    const type = t.fields?.Type || '';
    const prioNr = t.fields?.PrioNr || '';
    return (type.includes('Regie') || type.includes('review')) && prioNr !== 'Opgestuurd';
  });

  return (
    <Box>
      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#ede9fe' }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#8b5cf6' }}>{reviewTasks.length}</Typography>
            <Typography variant="caption" color="text.secondary">Reviews</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#dbeafe' }}>
            <Typography variant="h4" fontWeight={700} color="primary">{projects.length}</Typography>
            <Typography variant="caption" color="text.secondary">Projecten</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#dcfce7' }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#10b981' }}>
              {tasks.filter(t => !t.fields?.Afgerond).length}
            </Typography>
            <Typography variant="caption" color="text.secondary">Open taken</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Links */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <QuickLinkCard
              title="Meeting Notities"
              description="Vergaderverslagen & actiepunten"
              icon={AssignmentIcon}
              to="/MeetingDashboard"
              color="#8b5cf6"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <QuickLinkCard
              title="Projecten"
              description="Project overzicht"
              icon={FolderIcon}
              to="/projects"
              color="#3b82f6"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <QuickLinkCard
              title="Ondertitels"
              description="SRT Generator"
              icon={SubtitlesIcon}
              to="/SRTGenerator"
              color="#10b981"
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Review Tasks */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <MovieIcon sx={{ color: '#8b5cf6' }} />
              <Typography variant="subtitle1" fontWeight={700}>Creative Reviews</Typography>
            </Box>
            {reviewTasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                Geen reviews op dit moment
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {reviewTasks.slice(0, 6).map(task => (
                  <TaskCard key={task.id} task={task} compact onStatusUpdate={onStatusUpdate} showProjectName />
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Projects */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FolderIcon sx={{ color: '#3b82f6' }} />
              <Typography variant="subtitle1" fontWeight={700}>Mijn Projecten</Typography>
            </Box>
            <Grid container spacing={1.5}>
              {projects.slice(0, 4).map(project => (
                <Grid item xs={12} key={project.id}>
                  <ProjectCard
                    project={project}
                    taskCount={0}
                    completedCount={0}
                    projectStatus={projectStatuses?.[project.id]}
                    onClick={() => navigate(`/projects/${project.id}`)}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ===== MAIN ROLE DASHBOARD =====
function RoleDashboard() {
  const navigate = useNavigate();
  const { instance: msalInstance, accounts } = useMsal();
  const account = accounts?.[0];
  const userEmail = account?.username || '';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userDataUnavailable, setUserDataUnavailable] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState({});
  const [callsheets, setCallsheets] = useState([]);
  const [quitclaims, setQuitclaims] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [upcomingDraaidagen, setUpcomingDraaidagen] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [roleOverride, setRoleOverride] = useState(null); // Admin role switcher

  // Update task status in Airtable (via PrioNr field)
  const handleStatusUpdate = async (taskId, updates) => {
    // Vorige waarden vastleggen: het statusrondje is klein, een misklik zet een taak op Afgerond.
    const vorige = tasks.find((t) => t.id === taskId)?.fields || {};
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update task');
      }
      
      // Update local state
      setTasks(prev => prev.map(t => 
        t.id === taskId 
          ? { ...t, fields: { ...t.fields, PrioNr: updates.PrioNr, Afgerond: updates.Afgerond } }
          : t
      ));
      if (updates.Afgerond === true) {
        const terug = { PrioNr: vorige.PrioNr || 'Geen prio', Afgerond: !!vorige.Afgerond };
        biedOngedaanAan(`Taak → ${updates.PrioNr}`, async () => {
          const r = await authFetch(`${BACKEND_URL}/Api/tasks/${taskId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(terug),
          });
          if (!r.ok) throw new Error('Terugzetten mislukt');
          setTasks(prev => prev.map(t => (
            t.id === taskId ? { ...t, fields: { ...t.fields, ...terug } } : t
          )));
        });
      }
      
      return true;
    } catch (err) {
      console.error('Error updating task status:', err);
      toast.error('Fout bij bijwerken van taakstatus');
      return false;
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();

    // Safe JSON parser - checks content-type to avoid parsing HTML as JSON
    const safeJson = async (response) => {
      try {
        const ct = response.headers?.get?.('content-type') || '';
        if (ct && !ct.includes('application/json')) return null;
        return await response.json();
      } catch { return null; }
    };

    try {
      // PHASE 1: Essential data (user + tasks + projects) — show UI as soon as these arrive
      const [userResult, tasksResult, projectsResult] = await Promise.allSettled([
        authFetch(`${BACKEND_URL}/Api/personen?email=${encodeURIComponent(userEmail)}`),
        authFetch(`${BACKEND_URL}/Api/my-tasks?email=${encodeURIComponent(userEmail)}`),
        authFetch(`${BACKEND_URL}/Api/projects`),
      ]);

      // Process user data
      if (userResult.status === 'fulfilled' && userResult.value.ok) {
        const userData = await safeJson(userResult.value);
        if (userData) {
          const personenArray = Array.isArray(userData) ? userData : (userData.records || userData.people || []);
          const person = personenArray.find(p => p.fields?.Email?.toLowerCase() === userEmail.toLowerCase());
          if (person) {
            setUserData({
              id: person.id,
              naam: person.fields?.['Volledige naam'] || person.fields?.Naam || '',
              voornaam: person.fields?.Voornaam || '',
              functies: Array.isArray(person.fields?.Functie) ? person.fields.Functie : (typeof person.fields?.Functie === 'string' && person.fields.Functie ? person.fields.Functie.split(', ') : []),
              email: person.fields?.Email,
            });
            setUserDataUnavailable(false);
          }
        }
      } else if (userResult.status === 'fulfilled' && userResult.value.status === 503) {
        // AI Engine unavailable — signal to UI so it doesn't silently fall back to wrong role
        console.warn('[Dashboard] AI Engine unavailable — user data could not be loaded');
        setUserDataUnavailable(true);
      } else if (userResult.status === 'rejected') {
        console.warn('[Dashboard] User data fetch failed:', userResult.reason);
        setUserDataUnavailable(true);
      }

      // Process tasks
      if (tasksResult.status === 'fulfilled' && tasksResult.value.ok) {
        const tasksData = await safeJson(tasksResult.value);
        if (tasksData) setTasks(tasksData.tasks || tasksData || []);
      }

      // Process projects
      if (projectsResult.status === 'fulfilled' && projectsResult.value.ok) {
        const projectsData = await safeJson(projectsResult.value);
        if (projectsData) setProjects(projectsData.projects || projectsData.records || []);
      }

      // Show dashboard immediately — phase 1 done
      console.log(`[Dashboard] Phase 1 loaded in ${Math.round(performance.now() - t0)}ms`);
      setLoading(false);

      // PHASE 2: Secondary data (load in background, UI updates incrementally)
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0=Sun
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const weekStart = new Date(now); weekStart.setDate(now.getDate() + mondayOffset);
      const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
      const ws = weekStart.toISOString().slice(0, 10);
      const we = weekEnd.toISOString().slice(0, 10);

      const [statusesResult, callsheetsResult, quitclaimsResult, meetingsResult, draaidagenResult, deadlinesResult] = await Promise.allSettled([
        authFetch(`${BACKEND_URL}/Api/projects/statuses`),
        authFetch(`${BACKEND_URL}/Api/callsheets`),
        authFetch(`${BACKEND_URL}/Api/quitclaims`),
        authFetch(`${AI_ENGINE_URL}/api/v1/meetings/?user_email=${encodeURIComponent(userEmail)}`),
        authFetch(`${BACKEND_URL}/Api/draaidagen/upcoming`),
        authFetch(`${BACKEND_URL}/Api/my-deadlines?email=${encodeURIComponent(userEmail)}&startDate=${ws}&endDate=${we}`),
      ]);

      if (statusesResult.status === 'fulfilled' && statusesResult.value.ok) {
        const statusesData = await safeJson(statusesResult.value);
        if (statusesData) setProjectStatuses(statusesData.statuses || {});
      }

      if (callsheetsResult.status === 'fulfilled' && callsheetsResult.value.ok) {
        const callsheetsData = await safeJson(callsheetsResult.value);
        if (callsheetsData) setCallsheets(callsheetsData.records || callsheetsData || []);
      }

      if (quitclaimsResult.status === 'fulfilled' && quitclaimsResult.value.ok) {
        const quitclaimsData = await safeJson(quitclaimsResult.value);
        if (quitclaimsData) setQuitclaims(quitclaimsData.records || quitclaimsData || []);
      }

      if (meetingsResult.status === 'fulfilled' && meetingsResult.value.ok) {
        const meetingsData = await safeJson(meetingsResult.value);
        if (meetingsData) setMeetings(Array.isArray(meetingsData) ? meetingsData : meetingsData.meetings || []);
      }

      if (draaidagenResult.status === 'fulfilled' && draaidagenResult.value.ok) {
        const draaidagenData = await safeJson(draaidagenResult.value);
        if (draaidagenData) setUpcomingDraaidagen(draaidagenData.draaidagen || []);
      }

      if (deadlinesResult.status === 'fulfilled' && deadlinesResult.value.ok) {
        const deadlinesData = await safeJson(deadlinesResult.value);
        if (deadlinesData) setDeadlines(deadlinesData.byDate || []);
      }

      console.log(`[Dashboard] All data loaded in ${Math.round(performance.now() - t0)}ms`);

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      toast.error('Fout bij laden van dashboard gegevens');
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => registreerOngedaanSneltoets(), []);

  useEffect(() => {
    if (userEmail) {
      loadData();
    }
  }, [userEmail]);

  // Determine role (with admin override option)
  const detectedRole = getRoleCategory(userData?.functies);
  const role = roleOverride || detectedRole;
  const colors = roleColors[role];
  
  // Admin role options for switcher
  const roleOptions = [
    { value: '', label: `Auto (${detectedRole})` },
    { value: 'PL', label: '👤 Projectleider' },
    { value: 'UITVOERDER', label: '🎬 Uitvoerder' },
    { value: 'CREATIVE', label: '🎨 Creative' },
    { value: 'PRODUCER', label: '📋 Producer' },
    { value: 'MANAGEMENT', label: '📊 Management' },
  ];

  if (loading) {
    return (
      <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <CircularProgress size={24} />
          <Typography variant="h6" color="text.secondary">
            Dashboard laden...
          </Typography>
        </Box>
        <Skeleton variant="rectangular" height={100} sx={{ mb: 3, borderRadius: 2 }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      {/* Header */}
      <PageHeader
        fallbackText={getInitials(userData?.naam || userEmail)}
        accent={{ bg: colors.bg, color: colors.primary }}
        title={`${new Date().getHours() < 12 ? 'Goedemorgen' : new Date().getHours() < 18 ? 'Goedemiddag' : 'Goedenavond'}, ${userData?.voornaam || userEmail.split('@')[0]}!`}
        subtitle={userData?.functies?.length ? userData.functies.join(' · ') : undefined}
        rightActions={(
          <>
            {/* Admin Role Switcher */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="role-switch-label" sx={{ fontSize: '0.85rem' }}>
                🔧 View als
              </InputLabel>
              <Select
                labelId="role-switch-label"
                value={roleOverride || ''}
                label="🔧 View als"
                onChange={(e) => setRoleOverride(e.target.value || null)}
                sx={{
                  fontSize: '0.85rem',
                  backgroundColor: roleOverride ? '#fef3c7' : 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: roleOverride ? '#f59e0b' : '#e5e7eb',
                  },
                }}
              >
                {roleOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip title="Vernieuwen">
              <IconButton onClick={loadData}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      />

      {/* Admin override indicator */}
      {roleOverride && (
        <Box
          sx={{
            mt: -3,
            mb: 3,
            p: 1.5,
            backgroundColor: '#fef3c7',
            borderRadius: 2,
            border: '1px solid #f59e0b',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: '#92400e' }}>
            ⚠️ <strong>Admin modus:</strong> Je bekijkt het dashboard als <strong>{roleOptions.find(r => r.value === roleOverride)?.label}</strong>
          </Typography>
          <Button
            size="small"
            onClick={() => setRoleOverride(null)}
            sx={{ ml: 'auto', color: '#92400e', textTransform: 'none' }}
          >
            Terug naar eigen view
          </Button>
        </Box>
      )}

      {/* Warning when user data could not be loaded (AI Engine down) */}
      {userDataUnavailable && !userData && (
        <Paper
          sx={{
            p: 2, mb: 3, borderRadius: 2,
            backgroundColor: '#fef2f2', border: '1px solid #fecaca',
            display: 'flex', alignItems: 'center', gap: 2,
          }}
        >
          <WarningIcon sx={{ color: '#ef4444' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} color="#991b1b">
              Gebruikersgegevens niet beschikbaar
            </Typography>
            <Typography variant="body2" color="#7f1d1d">
              Je rol kon niet worden bepaald omdat de server tijdelijk onbereikbaar is. Het dashboard wordt mogelijk niet correct weergegeven.
            </Typography>
          </Box>
          <Button size="small" variant="outlined" color="error" onClick={loadData} startIcon={<RefreshIcon />}>
            Opnieuw laden
          </Button>
        </Paper>
      )}

      {/* Role-specific Dashboard — only render when user data is reliably loaded or admin override is active */}
      {(userData || roleOverride) ? (
        <>
          {role === 'PL' && (
            <PLDashboard user={userData} tasks={tasks} projects={projects} projectStatuses={projectStatuses} onStatusUpdate={handleStatusUpdate} msalInstance={msalInstance} account={account} meetings={meetings} onTaskCreated={(newTask) => setTasks(prev => [...prev, newTask])} draaidagen={upcomingDraaidagen} onRefreshDraaidagen={loadData} deadlines={deadlines} />
          )}
          {role === 'UITVOERDER' && (
            <UitvoerderDashboard user={userData} tasks={tasks} projects={projects} projectStatuses={projectStatuses} onStatusUpdate={handleStatusUpdate} draaidagen={upcomingDraaidagen} />
          )}
          {role === 'PRODUCER' && (
            <ProducerDashboard user={userData} tasks={tasks} projects={projects} projectStatuses={projectStatuses} callsheets={callsheets} quitclaims={quitclaims} onStatusUpdate={handleStatusUpdate} draaidagen={upcomingDraaidagen} />
          )}
          {role === 'CREATIVE' && (
            <CreativeDashboard user={userData} tasks={tasks} projects={projects} projectStatuses={projectStatuses} onStatusUpdate={handleStatusUpdate} />
          )}
          {role === 'MANAGEMENT' && (
            <PLDashboard user={userData} tasks={tasks} projects={projects} projectStatuses={projectStatuses} onStatusUpdate={handleStatusUpdate} msalInstance={msalInstance} account={account} meetings={meetings} onTaskCreated={(newTask) => setTasks(prev => [...prev, newTask])} draaidagen={upcomingDraaidagen} onRefreshDraaidagen={loadData} deadlines={deadlines} />
          )}
        </>
      ) : !userDataUnavailable ? null : null}
    </Box>
  );
}

export default RoleDashboard;
